import React, { useCallback, useRef, useState } from 'react';
import {
  SProgressBarContainer,
  SProgressBarTrack,
  SProgressBarBuffer,
  SProgressBarFilled
} from './styled';
import { formatTime } from './utils/formatTime';

const SEEK_STEP = 5;
const MAX_ANIMATED_STEP = 0.6;

interface IProps {
  currentTime: number;
  duration: number;
  buffered: number;
  isPlaying: boolean;
  onSeek: (time: number) => void;
}

const ProgressBar: React.FC<IProps> = ({
  currentTime,
  duration,
  buffered,
  isPlaying,
  onSeek
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const lastStepRef = useRef({ time: currentTime, smooth: false });

  // Only small forward steps glide; seeks and replays jump like a plain width change.
  if (lastStepRef.current.time !== currentTime) {
    const delta = currentTime - lastStepRef.current.time;
    lastStepRef.current = {
      time: currentTime,
      smooth: delta > 0 && delta < MAX_ANIMATED_STEP
    };
  }

  const getTimeFromPointer = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || !duration) return 0;

      const rect = track.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return percent * duration;
    },
    [duration]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setIsScrubbing(true);
      onSeek(getTimeFromPointer(e.clientX));
    },
    [getTimeFromPointer, onSeek]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pressure > 0) {
        onSeek(getTimeFromPointer(e.clientX));
      }
    },
    [getTimeFromPointer, onSeek]
  );

  const handlePointerEnd = useCallback(() => setIsScrubbing(false), []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      let newTime: number | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          newTime = Math.max(0, currentTime - SEEK_STEP);
          break;
        case 'ArrowRight':
          newTime = Math.min(duration, currentTime + SEEK_STEP);
          break;
        case 'Home':
          newTime = 0;
          break;
        case 'End':
          newTime = duration;
          break;
        default:
          return;
      }

      e.preventDefault();
      e.stopPropagation();
      onSeek(newTime);
    },
    [currentTime, duration, onSeek]
  );

  const playedPercent = duration ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration ? (buffered / duration) * 100 : 0;

  return (
    <SProgressBarContainer
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onLostPointerCapture={handlePointerEnd}
      onKeyDown={handleKeyDown}
      role="slider"
      aria-label="Video progress"
      aria-valuemin={0}
      aria-valuemax={Math.floor(duration)}
      aria-valuenow={Math.floor(currentTime)}
      aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
      tabIndex={0}
    >
      <SProgressBarTrack ref={trackRef}>
        <SProgressBarBuffer $percent={bufferedPercent} />
        <SProgressBarFilled
          $percent={playedPercent}
          $animate={isPlaying && !isScrubbing && lastStepRef.current.smooth}
        />
      </SProgressBarTrack>
    </SProgressBarContainer>
  );
};

export default ProgressBar;
